import { RESPONSIVE } from '@src/constants/responsive';
import type { MutableRefObject } from 'react';
import PdfWorker from './pdf/pdf.worker?worker';

// Workerのシングルトンインスタンス
let worker: Worker | null = null;
const pendingRequests = new Map<
  number,
  {
    resolve: (value: any) => void;
    reject: (reason: any) => void;
  }
>();
let requestIdCounter = 0;

// レンダリング状態を管理するためのMap
const renderingState = new Map<
  HTMLCanvasElement,
  { promise: Promise<void>; requestId: number; url: string; page: number }
>();

function getWorker(): Worker {
  if (!worker) {
    worker = new PdfWorker();
    worker.onmessage = (e) => {
      const { id, type, error, ...data } = e.data;
      const request = pendingRequests.get(id);

      if (request) {
        if (type === 'RENDER_SUCCESS' || type === 'PAGE_INFO_SUCCESS') {
          request.resolve(data);
        } else if (type === 'ERROR') {
          request.reject(error);
        }
        pendingRequests.delete(id);
      }
    };
  }
  return worker;
}

function sendWorkerMessage(message: any): Promise<any> {
  const worker = getWorker();
  const id = ++requestIdCounter;
  return new Promise((resolve, reject) => {
    pendingRequests.set(id, { resolve, reject });
    worker.postMessage({ ...message, id });
  });
}

/**
 * PDFiumを使用してPDFをCanvasにレンダリングする（Web Worker版）
 */
export default async function pdfiumWrapper(
  canvasRef: MutableRefObject<HTMLCanvasElement>,
  pdfUrl: string,
  pageNum: number,
  callback: (total: number, error?: { type: string; message: string }) => void
) {
  let canvas: HTMLCanvasElement;

  if (canvasRef.current === null || !pdfUrl) {
    callback(-1, { type: 'invalid_parameters', message: '無効なパラメータが指定されました' });
    return;
  }

  canvas = canvasRef.current;

  // 既存のレンダリング処理をチェック
  const existingRender = renderingState.get(canvas);
  if (existingRender) {
    if (existingRender.url === pdfUrl && existingRender.page === pageNum) {
      try {
        await existingRender.promise;
        return;
      } catch (_error) {
        renderingState.delete(canvas);
      }
    } else {
      renderingState.delete(canvas);
      const context = canvas.getContext('2d');
      if (context) {
        context.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  }

  const requestId = requestIdCounter + 1; // Note: sendWorkerMessage will increment it

  const renderPromise = (async () => {
    try {
      // 1. ページ情報の取得 (Get Page Info)
      const pageInfo = await sendWorkerMessage({
        type: 'GET_PAGE_INFO',
        pdfUrl,
        pageNum,
      });

      callback(pageInfo.totalPages);

      // 2. スケール計算 (Calculate Scale - Main Thread)
      const pageSize = { width: pageInfo.width, height: pageInfo.height };
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      let containerWidth: number;
      let containerHeight: number;

      if (screenWidth >= RESPONSIVE.BREAKPOINTS.DESKTOP_LARGE) {
        containerWidth = screenWidth * RESPONSIVE.PDF_SIZING.DESKTOP_LARGE.WIDTH_RATIO;
        containerHeight = screenHeight * RESPONSIVE.PDF_SIZING.DESKTOP_LARGE.HEIGHT_RATIO;
      } else if (screenWidth >= RESPONSIVE.BREAKPOINTS.DESKTOP_MEDIUM) {
        containerWidth = screenWidth * RESPONSIVE.PDF_SIZING.DESKTOP_MEDIUM.WIDTH_RATIO;
        containerHeight = screenHeight * RESPONSIVE.PDF_SIZING.DESKTOP_MEDIUM.HEIGHT_RATIO;
      } else if (screenWidth >= RESPONSIVE.BREAKPOINTS.TABLET) {
        containerWidth = screenWidth * RESPONSIVE.PDF_SIZING.TABLET.WIDTH_RATIO;
        containerHeight = screenHeight * RESPONSIVE.PDF_SIZING.TABLET.HEIGHT_RATIO;
      } else {
        containerWidth = screenWidth * RESPONSIVE.PDF_SIZING.MOBILE.WIDTH_RATIO;
        containerHeight = screenHeight * RESPONSIVE.PDF_SIZING.MOBILE.HEIGHT_RATIO;
      }

      const scaleX = (containerWidth - RESPONSIVE.PDF_CONTAINER_MARGIN) / pageSize.width;
      const scaleY = (containerHeight - RESPONSIVE.PDF_CONTAINER_MARGIN) / pageSize.height;

      let scale: number;
      if (screenWidth >= RESPONSIVE.BREAKPOINTS.TABLET) {
        scale = scaleX;
        scale = Math.max(
          RESPONSIVE.PDF_SIZING.DESKTOP_LARGE.SCALE_MIN,
          Math.min(scale, RESPONSIVE.PDF_SIZING.DESKTOP_LARGE.SCALE_MAX)
        );
      } else {
        scale = Math.min(scaleX, scaleY);
        scale = Math.max(
          RESPONSIVE.PDF_SIZING.MOBILE.SCALE_MIN,
          Math.min(scale, RESPONSIVE.PDF_SIZING.MOBILE.SCALE_MAX)
        );
      }

      const outputScale = window.devicePixelRatio || 1;
      const renderWidth = Math.floor(pageSize.width * scale);
      const renderHeight = Math.floor(pageSize.height * scale);

      canvas.width = Math.floor(renderWidth * outputScale);
      canvas.height = Math.floor(renderHeight * outputScale);

      canvas.style.width = `${renderWidth}px`;
      canvas.style.height = `${renderHeight}px`;
      if (!canvas.style.border) {
        canvas.style.border = '1px solid #000000';
      }

      // 3. レンダリング実行 (Render)
      const renderResult = await sendWorkerMessage({
        type: 'RENDER',
        pdfUrl,
        pageNum,
        scale: scale * outputScale,
      });

      // ガード処理: キャンバスの状態が最新のリクエストと一致するか確認
      // 別のリクエストが開始された場合、renderingStateのエントリが更新または削除されている
      const currentState = renderingState.get(canvas);
      if (!currentState || currentState.requestId !== requestId) {
        return;
      }

      const context = canvas.getContext('2d') as CanvasRenderingContext2D;
      const imageData = new ImageData(
        new Uint8ClampedArray(renderResult.imageData),
        renderResult.width,
        renderResult.height
      );
      context.putImageData(imageData, 0, 0);
    } catch (error: any) {
      if (error.type === 'token_expired') {
        callback(-1, {
          type: 'token_expired',
          message: 'PDFトークンの有効期限が切れています。再生成してください。',
        });
      } else if (error.type === 'timeout_error') {
        callback(-1, {
          type: 'timeout_error',
          message: 'PDFファイルの読み込みがタイムアウトしました',
        });
      } else if (error.type === 'network_error') {
        callback(-1, { type: 'network_error', message: 'PDFファイルの読み込みに失敗しました' });
      } else if (error.type) {
        callback(-1, { type: error.type, message: error.message || 'Error occurred' });
      } else {
        callback(-1, {
          type: 'unknown_error',
          message: 'PDFファイルの処理中にエラーが発生しました',
        });
      }
    } finally {
      renderingState.delete(canvas);
    }
  })();

  renderingState.set(canvas, {
    promise: renderPromise,
    requestId,
    url: pdfUrl,
    page: pageNum,
  });

  await renderPromise;
}

export async function preloadPdfiumLibrary(): Promise<void> {
  // Workerを初期化するだけで十分
  getWorker();
}

export function clearPdfiumCache(token?: string) {
  if (worker) {
    worker.postMessage({ type: 'CLEAR_CACHE', token });
  }
}
